import * as React from 'react';
import { matchPath } from 'react-router-dom';
import {
  ampli,
  IdentifyProperties,
  PreActionsProperties,
  PreConfirmationProperties,
  PreFormEditedProperties,
  PreFormReviewedProperties,
  ScreenViewedProperties,
} from '../../core/ampli';
import { ROUTES } from '../../core/routes';
import {
  AMPLITUDE_REPORT_TYPE,
  AMPLITUDE_SCREEN_NAMES,
  AMPLITUDE_TSB_AD_TYPE,
  InstallmentsEditType,
  PRE_FORM_REVIEWED,
  type AmplitudeTrackActions,
} from './types';

// Define the amplitude functions available for use through the hook
export const useAmplitude = () => {
  const [currentUuid, setCurrentUuid] = React.useState<string | null>(null);
  const [currentEmail, setCurrentEmail] = React.useState<string | null>(null);
  const [currentRole, setCurrentRole] =
    React.useState<IdentifyProperties['selected_org_staff_role']>(null);
  const [currentOrgId, setCurrentOrgId] = React.useState<string | null>(null);

  React.useEffect(() => {
    window.addEventListener('focus', identifyFocusHandler);
    return () => {
      window.removeEventListener('focus', identifyFocusHandler);
    };
  }, [currentUuid, currentEmail, currentRole, currentOrgId]);

  // Exposing method to identify user in Amplitude
  const amplitudeIdentify = (
    uuid: string,
    email: string,
    role: IdentifyProperties['selected_org_staff_role'],
    orgId: string
  ) => {
    ampli.identify(uuid, {
      email: email,
      selected_org_staff_role: role,
      selected_org_id: orgId,
    });
    setCurrentUuid(uuid);
    setCurrentEmail(email);
    setCurrentRole(role);
    setCurrentOrgId(orgId);
  };

  // Exposing methods to track events in Amplitude
  const amplitudeTrack: AmplitudeTrackActions = {
    preFormEdited: (editType: PreFormEditedProperties['pre_edit']) => {
      ampli.preFormEdited({
        pre_edit: editType,
      });
    },
    preActions: (pre_action: PreActionsProperties['pre_action']) => {
      ampli.preActions({
        pre_action,
      });
    },
    preFormReviewed: (reviewType: PreFormReviewedProperties['review_type']) => {
      ampli.preFormReviewed({
        review_type: reviewType,
      });
    },
    preInstallmentsEdited: (editType: InstallmentsEditType) => {
      ampli.preInstallmentsEdited({
        installment_edit_type: editType,
      });
    },
    preConfimation: (
      confirmationType: PreConfirmationProperties['pre_confirmation_action']
    ) => {
      ampli.preConfirmation({
        pre_confirmation_action: confirmationType,
      });
    },
    tsbHelpLinkClicked: ({
      organization_id,
      organization_name,
      page_viewed_url,
    }) => {
      ampli.tsbHelpLinkClicked({
        organization_id,
        organization_name,
        page_viewed_url,
      });
    },
    registrationWizardViewed: ({
      organization_id,
      organization_name,
      program_id,
      season_id,
    }) => {
      ampli.registrationWizardViewed({
        organization_id,
        organization_name,
        program_id,
        season_id,
      });
    },
    tsbFinancialsExported: (financialReport: AMPLITUDE_REPORT_TYPE) => {
      ampli.tsbFinancialsExported({
        financial_report: financialReport,
      });
    },
    surfaceViewed: (screenName: ScreenViewedProperties['screen_name']) =>
      ampli.screenViewed({
        screen_name: screenName,
        url: window?.location?.pathname,
      }),
    screenViewed: (path: string) => {
      handleScreenView(path);
    },
    tsbRegistrationActionClicked: ({ registration_action }) => {
      ampli.tsbRegistrationActionClicked({
        registration_action,
      });
    },
    tsbRegistrationExported: ({ registration_export_type }) => {
      ampli.tsbRegistrationExported({
        registration_export_type,
      });
    },
    tsbFinancialDateRangeChanged: ({
      financial_date_option,
      financial_report,
    }) => {
      ampli.tsbFinancialDateRangeChanged({
        financial_date_option,
        financial_report,
      });
    },
    tsbFinancialFilterChanged: (financialFilter) => {
      ampli.tsbFinancialFilterChanged({
        financial_filter: financialFilter.financial_filter,
        financial_report: financialFilter.financial_report,
        page_result_count: financialFilter.page_result_count,
      });
    },
    tsbFinancialActionActionClicked: ({
      financial_action,
      financial_report,
    }) => {
      ampli.tsbFinancialActionClicked({
        financial_action,
        financial_report,
      });
    },
    advertisementViewed: ({ ad }) => {
      ampli.clv1AdViewed({
        ad_type: ad,
      });
    },
    advertisementClicked: ({ ad }) => {
      ampli.clv1AdClickEvent({
        ad_type: ad,
      });
    },
    tsbAdClickAction: ({ adType, upsell_action }) => {
      switch (adType) {
        case AMPLITUDE_TSB_AD_TYPE.WEBSITE:
          ampli.tsbWebsiteAdClickEvent({
            ad_page_action: upsell_action,
          });
          break;
        case AMPLITUDE_TSB_AD_TYPE.COACHES_PACK:
          ampli.tsbCoachesPackAdClickEvent({
            ad_page_action: upsell_action,
          });
          break;
        case AMPLITUDE_TSB_AD_TYPE.SPONSORSHIP:
          ampli.tsbSponsorshipAdClickEvent({
            ad_page_action: upsell_action,
          });
          break;
        case AMPLITUDE_TSB_AD_TYPE.TOURNAMENT:
          ampli.tsbTournamentsAdClickEvent({
            ad_page_action: upsell_action,
          });
          break;
        default:
          break;
      }
    },
    adjustOrderActionClicked(action) {
      ampli.adjustOrderActions({ adjust_order_action: action });
    },
    adjustOrderBalanceChanged(action) {
      ampli.orderDetailReviewed({ review_type: action });
    },
    adjustOrderInstallmentsEdited(action) {
      ampli.orderDetailInstallmentsEdited({ installment_edit_type: action });
    },
  };
  const identifyFocusHandler = () => {
    if (currentUuid && currentEmail && currentRole && currentOrgId) {
      amplitudeIdentify(currentUuid, currentEmail, currentRole, currentOrgId);
    }
  };

  return { amplitudeIdentify, amplitudeTrack };
};

const getRouteKey = (path) => {
  const routes = Object.entries(ROUTES);
  for (const [key, pattern] of routes) {
    if (path === pattern) {
      return key;
    }
  }

  return null;
};

const handleScreenView = (path: string) => {
  const match = matchPath(path, {
    path: [...Object.keys(ROUTES).map((key) => ROUTES[key])],
    exact: true,
    strict: true,
  });

  if (match) {
    const routeKey = getRouteKey(match.path);

    const params = match.params as {
      orgId: boolean;
      formId?: string;
      step?: string;
      reportType?: string;
      memberTab?: string;
    };

    const reportType = params.reportType;
    const memberTab = params.memberTab;
    const screenName = AMPLITUDE_SCREEN_NAMES[getRouteKey(match.path)];

    switch (ROUTES[routeKey]) {
      case ROUTES.REGISTRATION_INDEX:
        /*
              Because of the way we handle the registration wizard,we don't want
              to track screen view since we already track "Registration Viewed"
            */
        if (!params.formId && !params.step && params.orgId) {
          ampli.screenViewed({
            screen_name: AMPLITUDE_SCREEN_NAMES[getRouteKey(match.path)],
            url: path,
          });
          return;
        } else if (
          params.formId === 'new' ||
          params.step == '0' ||
          !params.step
        ) {
          ampli.screenViewed({
            screen_name: AMPLITUDE_SCREEN_NAMES.REGISTRATION_PROGRAM_SELECT,
            url: path,
          });
          return;
        }
        break;
      case ROUTES.FINANCIALS:
        if (reportType) {
          const financialReportScreenName =
            reportType && reportType === 'outstanding'
              ? AMPLITUDE_SCREEN_NAMES.FINANCIALS_OUTSTANDING
              : reportType && reportType === 'deposits'
              ? AMPLITUDE_SCREEN_NAMES.FINANCIALS_DEPOSITS
              : AMPLITUDE_SCREEN_NAMES.FINANCIALS_TRANSACTIONS;

          if (!financialReportScreenName) break;

          ampli.screenViewed({
            screen_name: financialReportScreenName,
            url: path,
          });
        }
        break;
      case ROUTES.MEMBER:
        if (memberTab) {
          let memberProfileScreenName;
          switch (memberTab) {
            case 'participation':
              memberProfileScreenName =
                AMPLITUDE_SCREEN_NAMES.MEMBER_PROFILE_PARTICIPATION;
              break;
            case 'history':
              memberProfileScreenName =
                AMPLITUDE_SCREEN_NAMES.MEMBER_PROFILE_HISTORY;
              break;
            case 'invoices':
              memberProfileScreenName =
                AMPLITUDE_SCREEN_NAMES.MEMBER_PROFILE_INVOICES;
              break;
            case 'registrations':
              memberProfileScreenName =
                AMPLITUDE_SCREEN_NAMES.MEMBER_PROFILE_REGISTRATIONS;
              break;
            default:
              memberProfileScreenName = null;
              break;
          }

          if (!memberProfileScreenName) break;

          ampli.screenViewed({
            screen_name: memberProfileScreenName,
            url: path,
          });
        }
        break;
      default:
        if (!screenName) break;
        ampli.screenViewed({
          screen_name: AMPLITUDE_SCREEN_NAMES[getRouteKey(match.path)],
          url: path,
        });
        break;
    }
  }
};

export const getAmpliBalanceChangeType = (
  newBalance: number,
  currentBalance: number
) => {
  switch (true) {
    case newBalance === currentBalance:
      return PRE_FORM_REVIEWED.NO_BALANCE_CHANGE;
    case newBalance > currentBalance:
      return PRE_FORM_REVIEWED.BALANCE_INCREASE;
    case newBalance < currentBalance:
      return PRE_FORM_REVIEWED.BALANCE_DECREASE;
    default:
      return;
  }
};
